import React, { useEffect, Fragment, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { showToast } from "../../../components/ToastifyNotification";
import Swal from "sweetalert2";
import Breadcrumb from "../../../components/common/breadcrumb";

// ✅ For Excel & PDF
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // Correct import for pdf
import { GetAreas } from "../../../api/masters/areaAPI";
import { AreaWiseOpdRegistrationReportApi } from "../../../api/opd/opdReportsAPI";

const AreaWiseOpdRegistrationReport = () => {
    const [areas, setAreas] = useState([]);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [area, setArea] = useState([]);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const fetchAreas = async data => {
        dispatch({ type: "LOADER_START" });
        try {
            const response = await GetAreas(data); // Make sure login function returns token
            
            if (response.success) {
                setAreas(response.data);
            } else {
                // setError(response.message);
                showToast("error", response.message);
            }
        } catch (error) {
            // setError(error); // Handle login errors
            showToast("error", error);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    useEffect(() => {
        fetchAreas();
    }, []);

    const fetchOpds = async () => {
        dispatch({ type: "LOADER_START" });
        try {
            const response = await AreaWiseOpdRegistrationReportApi();
            if (response.success) {
                showToast("success", response.message);
                const formattedData = response.data.map((item, index) => ({
                    index: index + 1,
                    id: item._id,
                    type: item.docType || "",
                    opdNo: item.opdNo || "",
                    uhidNo: item.uhidNo || "",
                    casually: item.casually || "",
                    status: item.oldPatient === "Yes" ? "Old" : "New",
                    rawDate: item.date || "",
                    date: item.date
                        ? new Date(item.date).toLocaleDateString("en-IN")
                        : "",
                    patientName:
                        (item.patientNamePrefix ? item.patientNamePrefix + " " : "") +
                        (item.patientName || ""),
                    sex: item.sex || "",
                    ageValue: item.age || 0,
                    age: item.age ? `${item.age} ${item.ageSuffix || ""}` : "",
                    mobileNo: item.mobileNo || "",
                    address: item.address || "",
                    area: item.areaId?.name || "",
                    consultantDoctor: item.consultantDoctor || "",
                    department: item.departmentId?.name || "",
                    foreiger: item.foreiger || "No",
                    amount: item.amount || 0,
                    discountPercentage: item.discountPercentage || 0,
                    discountAmount: item.discountAmount || 0,
                    netAmount: item.netAmount || 0,
                    paymentMode: item.paymentMode || "",
                    createdBy: item.createdBy?.name || "",
                    createdAt: item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString("en-IN")
                        : "",
                    updatedBy: item.updatedBy?.name || "",
                    updatedAt: item.updatedAt
                        ? new Date(item.updatedAt).toLocaleDateString("en-IN")
                        : "",
                }));
                setData(formattedData);
                setFilteredData(formattedData);
            } else showToast("error", response.message);
        } catch (error) {
            showToast("error", error.message);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    useEffect(() => {
        fetchOpds();
    }, []);

    const groupedData = useMemo(() => {
        const groups = {};
        filteredData.forEach(item => {
            const key = item.area || "Unknown";
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
        });
        return groups;
    }, [filteredData]);

    // Filters
    useEffect(() => {
        let filtered = data;

        if (fromDate)
            filtered = filtered.filter(
                (item) => new Date(item.rawDate) >= new Date(fromDate)
            );
        if (toDate)
            filtered = filtered.filter(
                (item) => new Date(item.rawDate) <= new Date(toDate)
            );
       if (area.length > 0)
            filtered = filtered.filter(
                (item) => area.map(a => a.toLowerCase()).includes(item.area?.toLowerCase())
            );

        setFilteredData(filtered);
    }, [fromDate, toDate, area,data]);

    // Headers (used for CSV, Excel, PDF)
    const headers = [
        { label: "S.No", key: "index" },
        { label: "OPD No", key: "opdNo" },
        { label: "UHID No", key: "uhidNo" },
        { label: "Casually", key: "casually" },
        { label: "Status", key: "status" },
        { label: "Patient Name", key: "patientName" },
        { label: "Sex", key: "sex" },
        { label: "Address", key: "address" },
        { label: "Consultant Name", key: "consultantDoctor" },
        { label: "Department", key: "department" },
        { label: "G. Amount", key: "amount" },
        { label: "Discount %", key: "discountPercentage" },
        { label: "Discount Amt", key: "discountAmount" },
        { label: "Amount", key: "netAmount" },
        { label: "Pay. Mode", key: "paymentMode" },
        { label: "User", key: "createdBy" },
    ];

    // Columns (for table view)
    const columns = headers.map((h) => ({
        name: h.label,
        selector: (row) => row[h.key],
        sortable: true,
        width: "10%",
    }));

    // ---------- EXPORT EXCEL (GROUPED) ----------
const exportGroupedExcel = (groupedData) => {
  const sheetData = [];

  Object.keys(groupedData).forEach((area) => {
    sheetData.push({ Area: `${area}` });
    groupedData[area].forEach((row) => {
      sheetData.push({
        "OPD No": row.opdNo,
        "Patient Name": row.patientName,
        Sex: row.sex,
        Casually: row.casually,
        Status: row.status,
        "G.Amount": row.amount,
        "Discount Amt": row.discountAmount,
        "Net Amount": row.netAmount,
        "Pay.Mode": row.paymentMode,
      });
    });

    sheetData.push({
      "OPD No": "",
      "Patient Name": "",
      Sex: "",
      Casually: "",
      Status: "TOTAL",
      "G.Amount": groupedData[area].reduce((a, b) => a + Number(b.amount), 0),
      "Discount Amt": groupedData[area].reduce((a, b) => a + Number(b.discountAmount), 0),
      "Net Amount": groupedData[area].reduce((a, b) => a + Number(b.netAmount), 0),
    });

    sheetData.push({}); // blank row
  });

  const worksheet = XLSX.utils.json_to_sheet(sheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Area Wise OPD");
  XLSX.writeFile(workbook, "AreaWise_OPD.xlsx");
};

// ---------- EXPORT PDF (GROUPED) ----------
const exportGroupedPDF = (groupedData) => {
  const doc = new jsPDF();
  doc.setFontSize(14);
  doc.text("Area Wise OPD Report", 14, 10);
  let yPos = 20;

  Object.keys(groupedData).forEach((area) => {
    doc.setFontSize(12);
    doc.text(`Area: ${area}`, 14, yPos);
    yPos += 5;

    autoTable(doc, {
      startY: yPos,
      head: [["OPD No", "Patient", "Sex", "Casually", "Status", "Net Amt"]],
      body: groupedData[area].map((row) => [
        row.opdNo,
        row.patientName,
        row.sex,
        row.casually,
        row.status,
        row.netAmount,
      ]),
    });

    // 🔥 FIX : LAST TABLE Y POSITION
    yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 5 : yPos + 10;

    doc.setFontSize(11);
    doc.text(
      `Total Net Amount: ₹${groupedData[area].reduce((a, b) => a + Number(b.netAmount), 0)}`,
      14,
      yPos
    );
    yPos += 10;
  });

  doc.save("AreaWise_OPD.pdf");
};

    const resetFilters = () => {
        setFromDate("");
        setToDate("");
        setArea("");
    };

    return (
        <Fragment>
            <Breadcrumb parent="OPD" title="Area Wise OPD Registration Report" />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <h5>Area Wise OPD Registration Report</h5>

                                <div className="row mt-3">
                                    {/* Filters */}
                                    <div className="col-sm-2">
                                        <label>From Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-2">
                                        <label>To Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-sm-2">
                                        <select
                                            className="form-control"
                                            value={area}
                                            onChange={(e) => setArea([...e.target.selectedOptions].map(opt => opt.value))}
                                            multiple
                                        >
                                            {areas.map((area) => (
                                                <option key={area._id} value={area.name}>
                                                    {area.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Export Buttons */}
                                    <div
                                        className="col-sm text-end"
                                        style={{ display: "flex", justifyContent: "flex-end" }}
                                    >
                                        <button
                                            className="btn btn-success mt-4 me-2"
                                            onClick={()=>exportGroupedExcel(groupedData)}
                                        >
                                            Excel
                                        </button>
                                        <button
                                            className="btn btn-danger mt-4 me-2"
                                            onClick={()=>exportGroupedPDF(groupedData)}
                                        >
                                            PDF
                                        </button>
                                        <button
                                            className="btn btn-warning mt-4"
                                            onClick={resetFilters}
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">

                                {/* Group Wise OPD Tables */}
                                {Object.keys(groupedData).map((areaName, index) => (
                                    <div key={index} className="mb-4">
                                        <h5 className="text-primary fw-bold">{areaName}</h5>

                                        <DataTable
                                            columns={columns}
                                            data={groupedData[areaName]}
                                            highlightOnHover
                                            dense
                                            pagination={false}
                                        />
                                        <div className="mt-2">
                                            <strong>Total Patients:</strong> {groupedData[areaName].length} |
                                            <strong> Total Net Amount:</strong> ₹{groupedData[areaName].reduce((sum,x)=>sum + Number(x.netAmount||0),0)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default AreaWiseOpdRegistrationReport;
